import React from 'react';
import { Spin } from 'antd';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { ProjectsActions } from 'redux/actions';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { SecondaryButton, SpaceBetween, Container, PrimaryButtonV2, H1 } from 'utils';


// Local imports
import {
  PageWrapper,
  ContentWrapper,
  HeaderWrapper,
  SkipLink,
  NavigationWrapper,
  IntroText,
  SubText,
  NoteText,
  InfoLinkWrapper,
  InfoLink,
} from './styles';
import usePromotions from './usePromotions';
import CategorySection from './CategorySection';
import InfoModal from './InfoModal';
import DetailModal from './DetailModal';
import { QuestionIcon } from './icons';

const Promotions = ({
  projectId,
  handleExitEditForm,
  editProject,
}) => {
  const dispatch = useDispatch();

  // Redux state
  const { project, loading: reduxLoading } = useSelector((state) => state.projects);

  // Redux actions
  const handleSteps = (step) => dispatch(ProjectsActions.handleSteps(step));
  const updateDraft = (id, data) => dispatch(ProjectsActions.updateDraft(id, data));

  // Custom hook for promotions logic
  const {
    loading,
    categories,
    selectedPromotionIds,
    showInfoModal,
    showDetailModal,
    selectedPromotion,
    getPromotionsByCategory,
    openInfoModal,
    closeInfoModal,
    closeDetailModal,
    handlePromotionSelect,
    confirmPromotionSelection,
    savePromotions,
  } = usePromotions(project, projectId, updateDraft);

  // Navigation handlers
  const handleReturn = () => {
    if (editProject) {
      handleExitEditForm();
    } else {
      handleSteps(7);
    }
  };

  const handleContinue = () => {
    savePromotions();
    if (editProject) {
      handleSteps(19);
    } else {
      handleSteps(19);
    }
  };

  const handleSkip = () => {
    handleContinue();
  };

  return (
    <PageWrapper>
      <Container className="inside-form-container">
        <Spin spinning={reduxLoading || loading}>
          <ContentWrapper>
            <HeaderWrapper>
              <H1>Promotions</H1>
              <SkipLink onClick={handleSkip}>Überspringen</SkipLink>
            </HeaderWrapper>

            <IntroText>
              <strong>Optional:</strong> Erhöhe deine Sichtbarkeit innerhalb der
              deutsch-muslimischen Community.
            </IntroText>

            <SubText>
              Du kannst diesen Schritt überspringen. Dein Projekt wird auch ohne
              Promotion regulär gelistet.
            </SubText>

            {/*<SubTextSecond>*/}
            {/*  Auch ohne Auswahl wirst du regulär gelistet und profitierst von*/}
            {/*  der commonsplace-Community und der Plattform-Dynamik.*/}
            {/*</SubTextSecond>*/}

            <InfoLinkWrapper onClick={openInfoModal}>
              <QuestionIcon />
              <InfoLink>Was sind Promotions?</InfoLink>
            </InfoLinkWrapper>

            <NoteText>
              Preise gelten für Organisationen und Vereine.
              <br />
              Konditionen für Privatpersonen auf Anfrage.
            </NoteText>

            {/* Category Sections */}
            {categories.map((category) => (
              <CategorySection
                key={category.id}
                category={category}
                promotions={getPromotionsByCategory(category.id)}
                selectedPromotionIds={selectedPromotionIds}
                onSelectPromotion={handlePromotionSelect}
              />
            ))}
          </ContentWrapper>

          <NavigationWrapper>
            <SpaceBetween>
              <SecondaryButton
                type="button"
                minWidth="120px"
                onClick={handleReturn}
              >
                {editProject ? (
                  <p>Zurück zur Projektübersicht</p>
                ) : (
                  <>
                    <ArrowLeftOutlined />
                    <p>Zurück</p>
                  </>
                )}
              </SecondaryButton>

              <PrimaryButtonV2
                type="primary"
                minWidth="120px"
                onClick={handleContinue}
              >
                {editProject ? (
                  <p>Speichern*</p>
                ) : (
                  <>
                    <p>Weiter</p>
                    <ArrowRightOutlined />
                  </>
                )}
              </PrimaryButtonV2>
            </SpaceBetween>
          </NavigationWrapper>

          {/* Modals */}
          <InfoModal open={showInfoModal} onClose={closeInfoModal} />
          <DetailModal
            open={showDetailModal}
            promotion={selectedPromotion}
            selectedPromotionIds={selectedPromotionIds}
            onClose={closeDetailModal}
            onConfirm={confirmPromotionSelection}
            onDeselect={() => {
              // Deselect promotion
              const newSelectedIds = selectedPromotionIds.filter(
                (id) => id !== selectedPromotion.id
              );
              updateDraft(projectId, { selected_promotion_ids: newSelectedIds });
              closeDetailModal();
            }}
          />
        </Spin>
      </Container>
    </PageWrapper>
  );
};

Promotions.propTypes = {
  projectId: PropTypes.string,
  handleExitEditForm: PropTypes.func,
  editProject: PropTypes.bool,
  setCanMove: PropTypes.func.isRequired,
};

Promotions.defaultProps = {
  projectId: null,
  handleExitEditForm: () => {},
  editProject: false,
};

export default Promotions;
